import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Stack } from '@mui/system';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import countries from '@/shared/configs/countries.json';
import ProfilesTemplateSchemas from '@/shared/types/shipping/profilesTemplateSchemas.type';

import useAddFirstZone from './hooks/useAddFirstZone';

type AddFirstZoneProps = {
  addFirstZoneState: ReturnType<typeof useAddFirstZone>;
  templateSchemas: ProfilesTemplateSchemas;
};

const AddFirstZone: FC<AddFirstZoneProps> = ({
  addFirstZoneState,
  templateSchemas,
}) => {
  const { t } = useTranslation(['shipping', 'forms']);

  const { addZoneForm, formErrors, setAddZoneForm } = addFirstZoneState;

  const getCountriesOptions = () => {
    return (
      countries
        .find((country) => country.code === addZoneForm.delivery_country)
        ?.provinces.map((province) => ({
          label: province.name,
          value: province.code,
        })) || []
    );
  };

  return (
    <Stack py={2}>
      <WaveUIForm_V2
        fields={[
          {
            field_type: FieldType.Select,
            field_props: {
              required: true,
              name: 'delivery_country',
              label: t('forms:delivery_country'),
              placeholder: t('forms:delivery_country'),
              value: addZoneForm.delivery_country,
              options: countries.map((country) => ({
                label: country.country,
                value: country.code,
              })),
              error: !!formErrors?.delivery_country,
              helperText: formErrors?.delivery_country,
            },
          },
          {
            field_type: FieldType.Select,
            field_props: {
              required: true,
              name: 'deliver_state',
              label: t('forms:deliver_state'),
              placeholder: t('forms:deliver_state'),
              value: addZoneForm.deliver_state,
              options: getCountriesOptions(),
              error: !!formErrors?.deliver_state,
              helperText: formErrors?.deliver_state,
            },
          },
          {
            field_type: FieldType.Select,
            field_props: {
              required: true,
              name: 'shipping_rate_type',
              label: t('forms:shipping_rate_type'),
              placeholder: t('forms:shipping_rate_type'),
              value: addZoneForm.shipping_rate_type,
              options: templateSchemas.rate_types.map((item) => {
                const label: Record<string, string> = {
                  flat_rate: 'Flat Rate',
                };

                return {
                  label: label[item.shipping_rate_type],
                  value: item.shipping_rate_type,
                };
              }),
              error: !!formErrors?.shipping_rate_type,
              helperText: formErrors?.shipping_rate_type,
            },
          },
          {
            field_type: FieldType.Number,
            field_props: {
              name: 'shipping_rate',
              label: t('forms:shipping_rate'),
              placeholder: t('forms:shipping_rate'),
              value: addZoneForm.shipping_rate,
              error: !!formErrors?.shipping_rate,
              helperText: formErrors?.shipping_rate,
            },
          },
          {
            columns: 6,
            field_type: FieldType.Switch,
            field_props: {
              name: 'exclusionary',
              label: t('forms:exclusionary'),
              checked: addZoneForm.exclusionary,
            },
          },
        ]}
        getFieldsValues={(value) => {
          setAddZoneForm((prev) => {
            if (value.delivery_country) {
              const country = countries.find(
                (country) => country.code === value.delivery_country,
              );
              value.deliver_state = country?.provinces[0].code;
            }

            return { ...prev, ...value };
          });
        }}
      />
    </Stack>
  );
};

export default AddFirstZone;
